import { type RouteLocationNormalizedLoaded, type RouteLocationRaw, useRoute } from 'vue-router';
import { reactive, ref } from 'vue';

// A route's meta.breadcrumb names the screens above the record; only the View knows the record
// itself (a customer's name, a contract's reference) once its data has loaded. The View hands those
// crumbs over here and AppBreadcrumb renders them after the route's own. AppBreadcrumb clears them
// when the route name or its params change, so a label never outlives its record.
export interface Crumb {
    label: string;
    to?: RouteLocationRaw;
    //the List the crumb leads back to, as the List's route names it in meta.list
    list?: string;
}

export const recordCrumbs = ref<Crumb[]>([]);

// The path the user last had open in each List, its filters, page and sort included, so a crumb back
// to the List returns there. In memory only: a reload starts every List from its defaults again.
const listLocations = reactive(new Map<string, string>());

export function rememberListLocation(list: string, fullPath: string) {
    listLocations.set(list, fullPath);
}

export function crumbTarget(crumb: Crumb): RouteLocationRaw | undefined {
    return (crumb.list && listLocations.get(crumb.list)) || crumb.to;
}

//the record a route shows: its name and params, not its query, so paging or an opened panel keeps the crumbs
export const recordKey = (route: RouteLocationNormalizedLoaded) => `${String(route.name)}|${JSON.stringify(route.params)}`;

export function clearRecordCrumbs() {
    recordCrumbs.value = [];
}

// Called in a View's setup. forRoute() is called when a fetch starts and binds its crumbs to the record
// the route shows at that moment: a response that lands after the user moved on, to another screen or to
// the next contract in the same View, is dropped instead of labelling the wrong page.
export function useBreadcrumb() {
    const route = useRoute();

    return {
        forRoute() {
            const key = recordKey(route);

            return (crumbs: Crumb[]) => {
                if (recordKey(route) === key) recordCrumbs.value = crumbs;
            };
        }
    };
}
